import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

// One cart-error fallback for both PDPs (dm-personalizer.js CART_ERROR_FALLBACK, parity r2
// A7). Server messages pass through verbatim.
const CART_ERROR_FALLBACK = 'Unable to add this to your cart right now. Please try again.';

/**
 * dm-standard-form — the buy form on the standard (non-personalized) PDP.
 *
 * The standard-product counterpart to <dm-personalizer>. It owns option selection,
 * variant resolution, the "Pair it with" upsells and the cart POST, and publishes the
 * same read-only `snapshot` shape so the sticky bar and the pup's room (<dm-room>) can
 * subscribe to either form without knowing which template it is on.
 *
 * Variant resolution happens in the browser from the payload the snippet renders, so
 * changing a colour never costs a round trip.
 *
 * Cart contract (mirrors the personalized template so the drawer flow is identical):
 *   · ONE /cart/add.js items[] call — the product, every toggled upsell, every room pick.
 *   · The main line carries `_dm_template: 'dm-standard'`.
 *   · Upsell lines and room product picks carry `_dm_addon_for` naming the parent product.
 *   · /cart/add.js is atomic, so a rejected batch is retried item by item rather than
 *     discarding the customer's selection.
 *
 * Motion (206:6958): loading 200ms → success "Added!" 300ms → reset after 2000ms.
 *
 * Fallback: if the variant payload is missing or unparseable, the form never strands the
 * customer behind a dead "Unavailable" CTA. It falls back to the server-rendered variant
 * (data-fallback-variant-id / -price) for the server-rendered selection, so the CTA stays
 * enabled and posts that id. Any other selection cannot be resolved and reads Unavailable
 * rather than posting the wrong variant.
 */
const ADDED_HOLD_MS = 2000;
const FLASH_MS = 150;

class DmStandardForm extends Component {
  requiredRefs = ['cta'];

  /** @type {{ id: number, options: string[], available: boolean, price: number, priceFormatted: string, compareAtPrice: number | null, compareAtFormatted: string, title: string, featuredMedia: string | null, thumb?: string | null, inventoryTracked?: string, inventoryPolicy?: string, inventoryQuantity?: number }[]} */
  #variants = [];

  /** @type {string[]} */
  #selected = [];

  /** @type {number | null} */
  #variantId = null;
  /** @type {string | null} */
  #colorwayColor = null;

  /** True when the payload could not be read and the server variant stands in. */
  #fallback = false;

  #submitting = false;
  #added = false;
  #addedTimer = 0;

  /** Picked room tiles, from dm:addons-change (dm-personalizer.js reads the same event). */
  #roomCents = 0;

  /**
   * Announced with dm:gating-change, as the personalizer does. Never dm:state: the room renders
   * on that and sends this event again.
   */
  #onAddonsChange = (/** @type {Event} */ event) => {
    this.#roomCents = Number(/** @type {CustomEvent} */ (event).detail?.totalCents) || 0;
    this.#renderCta();
    document.dispatchEvent(new CustomEvent('dm:gating-change'));
  };

  connectedCallback() {
    super.connectedCallback();

    this.#readVariants();
    this.#readSelection();
    if (!this.#variants.length) this.#useFallbackVariant();
    this.#resolve();

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('dm:addons-change', this.#onAddonsChange);

    this.#render();
    this.#publish();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('dm:addons-change', this.#onAddonsChange);
    window.clearTimeout(this.#addedTimer);
  }

  // ------------------------------------------------------------- public API

  /**
   * Read-only view for subscribers (the sticky bar). `mode` lets a subscriber tell the
   * two templates apart without querying for element names.
   */
  get snapshot() {
    const variant = this.#variant();
    return {
      mode: 'standard',
      variantId: this.#variantId ? String(this.#variantId) : '',
      // A default-variant-only product has no option to name ("Default Title" is never shown).
      variantTitle: this.#selected.length ? variant?.title || '' : '',
      // The variant's featured photo at 120w: the sticky thumb follows the stage (C-8).
      variantPhotoUrl: variant?.thumb || '',
      optionsLabel: this.#selected.filter(Boolean).join(' · '),
      available: Boolean(variant?.available),
      canSubmit: this.#canSubmit(),
      submitting: this.#submitting,
      totalFormatted: this.#formattedTotal(),
      variantPrice: variant?.price || 0,
      upsellCents: this.#upsellCents(),
      // Present so a subscriber written against the personalized snapshot reads
      // sensible empties rather than undefined.
      petName: '',
      toyColorLabel: '',
      embroideryLabel: '',
    };
  }

  /** The sticky bar's Add to cart routes here — same gating as the inline button. */
  submit() {
    return this.#addToCart();
  }

  // --------------------------------------------------------------- selection

  #onChange = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.matches('[data-dm-option-value]')) {
      this.#readSelection();
      this.#resolve();
      this.#render();
      this.#publish();
      this.#emitVariantChange(/** @type {HTMLInputElement} */ (target));
      this.#syncColorwayName();
      return;
    }

    if (target.matches('[data-dm-upsell-drive]')) {
      const card = target.closest('[data-dm-upsell]');
      if (card instanceof HTMLElement) {
        this.#renderUpsell(card);
        this.#render();
        this.#publish();
      }
    }
  };

  #onClick = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const cta = target.closest('[data-dm-std-cta]');
    if (cta) {
      this.#addToCart();
      return;
    }

    const retry = target.closest('[ref="retry"]');
    if (retry) {
      this.#setCartError('');
      this.#addToCart();
      return;
    }

    const change = target.closest('[data-dm-upsell-change]');
    if (change instanceof HTMLElement) {
      const card = change.closest('[data-dm-upsell]');
      const options = card?.querySelector('[data-dm-upsell-options]');
      if (options instanceof HTMLElement) {
        const open = options.hidden;
        options.hidden = !open;
        change.setAttribute('aria-expanded', String(open));
      }
      return;
    }

    const add = target.closest('[data-dm-upsell-add]');
    if (add instanceof HTMLElement && !(/** @type {HTMLButtonElement} */ (add).disabled)) {
      const on = add.getAttribute('aria-pressed') !== 'true';
      add.setAttribute('aria-pressed', String(on));

      const label = add.querySelector('[data-dm-upsell-add-label]');
      const icon = add.querySelector('.dm-upsell__add-icon');
      if (label) label.textContent = on ? 'Added' : 'Add';
      if (icon) icon.textContent = on ? '✓' : '+';

      // Border flash on tap (motion 206:6967).
      add.dataset.flash = 'true';
      window.setTimeout(() => delete add.dataset.flash, FLASH_MS);

      this.#render();
      this.#publish();
    }
  };

  /** Read every option field's checked radio into positional order. */
  #readSelection() {
    /** @type {string[]} */
    const next = [];
    for (const field of this.querySelectorAll('[data-dm-option-field]')) {
      const element = /** @type {HTMLElement} */ (field);
      const index = Number(element.dataset.optionIndex) || 0;
      const checked = /** @type {HTMLInputElement | null} */ (
        element.querySelector('[data-dm-option-value]:checked')
      );
      next[index] = checked?.value || '';
    }
    this.#selected = next;
  }

  /** Resolve the variant whose option values match the current selection exactly. */
  #resolve() {
    // A product with only Shopify's default variant renders no option field (nothing to
    // choose; "Default Title" is never shown), so that one variant is the selection.
    if (!this.#selected.length && this.#variants.length === 1) {
      this.#variantId = this.#variants[0].id;
      return;
    }

    const match = this.#variants.find(
      (variant) =>
        variant.options.length === this.#selected.length &&
        variant.options.every((value, index) => value === this.#selected[index])
    );
    this.#variantId = match ? match.id : null;
  }

  #variant() {
    return this.#variants.find((variant) => variant.id === this.#variantId) || null;
  }

  #canSubmit() {
    const variant = this.#variant();
    return Boolean(variant?.available) && !this.#submitting;
  }

  // ---------------------------------------------------------------- rendering

  #render() {
    this.#renderPrice();
    this.#renderReadout();
    this.#renderAvailability();
    this.#renderCta();
    for (const card of this.querySelectorAll('[data-dm-upsell]')) {
      this.#renderUpsell(/** @type {HTMLElement} */ (card));
    }
  }

  /**
   * The price block lives in the hero's info column, outside this element, so it is
   * resolved from the section rather than from `this`.
   */
  #renderPrice() {
    // In fallback mode the server already rendered this variant's price, compare-at and
    // save pill; the stand-in carries no compare-at, so leave the block untouched.
    if (this.#fallback) return;

    const section = this.closest('.dm-hero') || document;
    const block = section.querySelector('[data-dm-price]');
    if (!block) return;

    const current = block.querySelector('[data-dm-price-current]');
    const compare = block.querySelector('[data-dm-price-compare]');
    const save = block.querySelector('[data-dm-price-save]');

    const variant = this.#variant();
    if (!variant) {
      // A combination that does not exist: never leave the previous variant's strike
      // price and savings beside a CTA that reads Unavailable.
      if (compare instanceof HTMLElement) compare.hidden = true;
      if (save instanceof HTMLElement) save.hidden = true;
      return;
    }

    if (current) current.textContent = variant.priceFormatted;

    const onSale = Boolean(variant.compareAtPrice && variant.compareAtPrice > variant.price);
    if (compare instanceof HTMLElement) {
      compare.hidden = !onSale;
      if (onSale) compare.textContent = variant.compareAtFormatted;
    }
    if (save instanceof HTMLElement) {
      save.hidden = !onSale;
      if (onSale) save.textContent = `Save ${this.#money((variant.compareAtPrice || 0) - variant.price)}`;
    }
  }

  /** The swatch field shows the chosen value's name under the row. */
  #renderReadout() {
    for (const field of this.querySelectorAll('[data-dm-option-field]')) {
      const element = /** @type {HTMLElement} */ (field);
      const readout = element.querySelector('[data-dm-option-readout]');
      if (!readout) continue;
      const index = Number(element.dataset.optionIndex) || 0;
      readout.textContent = this.#selected[index] || '';
    }
  }

  /**
   * Mark every option value that cannot be bought with the rest of the CURRENT selection
   * (fix-plan §2.3 Unavailable, C2-3). Liquid paints the first frame with the same rule
   * (dm-option-field.liquid); this keeps it true after every change, so a value marked
   * Sold out never enables the CTA and an unmarked value never leads to a Sold out or
   * Unavailable CTA. Inputs stay selectable (the standard contract): choosing a marked
   * value shows why, and re-marks the other fields for that value.
   */
  #renderAvailability() {
    // The stand-in variant only knows the server selection; leave Liquid's marks alone.
    if (this.#fallback || !this.#variants.length) return;

    const soldOutText = this.dataset.soldoutLabel || 'Sold out';
    const unavailableText = this.dataset.unavailableLabel || 'Unavailable';

    for (const field of this.querySelectorAll('[data-dm-option-field]')) {
      const index = Number(/** @type {HTMLElement} */ (field).dataset.optionIndex) || 0;

      for (const element of field.querySelectorAll('[data-dm-option-value]')) {
        const input = /** @type {HTMLInputElement} */ (element);
        const label = input.closest('label');
        if (!label) continue;

        const state = this.#valueState(index, input.value);
        const unbuyable = state !== 'available';
        const text = state === 'missing' ? unavailableText : soldOutText;

        if (unbuyable) input.dataset.soldout = 'true';
        else delete input.dataset.soldout;

        if (label.classList.contains('dm-swatch')) {
          label.classList.toggle('dm-swatch--soldout', unbuyable);
          label.title = unbuyable ? `${input.value} — ${text.toLowerCase()}` : input.value;
          const name = label.querySelector('[data-dm-option-sr]');
          if (name) name.textContent = unbuyable ? `${input.value} (${text.toLowerCase()})` : input.value;
          continue;
        }

        label.classList.toggle('dm-optcard--soldout', unbuyable);
        let line = label.querySelector('.dm-optcard__sub--soldout');
        if (unbuyable) {
          if (!line) {
            line = document.createElement('span');
            line.className = 'dm-optcard__sub dm-optcard__sub--soldout';
            (label.querySelector('.dm-optcard__text') || label).append(line);
          }
          if (line.textContent !== text) line.textContent = text;
        } else {
          line?.remove();
        }
      }
    }
  }

  /**
   * What choosing `value` for option `index` would resolve to, every other option held at
   * its current value: 'available', 'soldout', or 'missing' (no such variant).
   * @param {number} index
   * @param {string} value
   */
  #valueState(index, value) {
    const variant = this.#variants.find(
      (candidate) =>
        candidate.options.length === this.#selected.length &&
        candidate.options[index] === value &&
        candidate.options.every((option, i) => i === index || option === this.#selected[i])
    );
    if (!variant) return 'missing';
    return variant.available ? 'available' : 'soldout';
  }

  #renderCta() {
    const cta = this.refs.cta;
    if (!(cta instanceof HTMLButtonElement)) return;

    const variant = this.#variant();

    /** @type {string} */
    let state = 'default';
    if (this.#added) state = 'added';
    else if (this.#submitting) state = 'loading';
    else if (!variant) state = 'unavailable';
    else if (!variant.available) state = 'soldout';

    this.#renderScarcity(variant, state);

    cta.dataset.state = state;
    cta.disabled = state !== 'default';
    cta.setAttribute('aria-busy', String(state === 'loading'));

    const addLabel = this.dataset.addLabel || 'Add to cart';
    const labels = {
      default: variant ? `${addLabel} — ${this.#formattedTotal()}` : addLabel,
      loading: 'Adding…',
      added: 'Added!',
      soldout: this.dataset.soldoutLabel || 'Sold out',
      unavailable: this.dataset.unavailableLabel || 'Unavailable',
    };
    cta.textContent = labels[state];
  }

  /**
   * The scarcity line describes the selected variant, with the SAME rule dm-personalizer.js
   * #renderScarcity applies (logic-scarcity-rule-forked): a real low-stock count when Shopify
   * tracks the variant, denies overselling and holds 0 < quantity ≤ threshold ("Only 3
   * left"); otherwise the computed ship-day line. It hides while nothing shippable is
   * selected. Never an invented number.
   * @param {{ id: number, inventoryTracked?: string, inventoryPolicy?: string, inventoryQuantity?: number } | null} variant
   * @param {string} state
   */
  #renderScarcity(variant, state) {
    const scarcity = this.refs.scarcity;
    if (!(scarcity instanceof HTMLElement)) return;

    // Fallback mode knows no inventory: keep the server-rendered line for its variant.
    if (!this.#fallback && variant) {
      const text = this.#lowStockText(variant) || this.dataset.scarcityText || '';
      if (scarcity.textContent !== text) scarcity.textContent = text;
      scarcity.dataset.dmScarcityVariant = String(variant.id);
    }

    scarcity.hidden = state === 'unavailable' || state === 'soldout' || !scarcity.textContent?.trim();
  }

  /**
   * @param {{ inventoryTracked?: string, inventoryPolicy?: string, inventoryQuantity?: number }} variant
   */
  #lowStockText(variant) {
    const threshold = Number(this.dataset.lowStockThreshold) || 0;
    if (this.dataset.showLowStock !== 'true' || threshold <= 0) return '';
    if (!variant.inventoryTracked || variant.inventoryPolicy !== 'deny') return '';
    const quantity = Number(variant.inventoryQuantity) || 0;
    return quantity > 0 && quantity <= threshold ? `Only ${quantity} left` : '';
  }

  /** @param {HTMLElement} card */
  #renderUpsell(card) {
    const variants = this.#upsellVariants(card);
    if (!variants.length) return;

    const driveIndex = Number(card.dataset.driveIndex) || 0;
    const base = variants.find((variant) => String(variant.id) === card.dataset.defaultVariant) || variants[0];
    const checked = /** @type {HTMLInputElement | null} */ (
      card.querySelector('[data-dm-upsell-drive]:checked')
    );

    // Every option except the driving one is pinned to the default variant, so variant
    // ORDER in Shopify admin controls the default.
    const wanted = [...base.options];
    if (checked) wanted[driveIndex] = checked.value;

    const match = variants.find(
      (variant) => variant.options.length === wanted.length && variant.options.every((value, i) => value === wanted[i])
    );

    card.dataset.variantId = match ? String(match.id) : '';

    const price = card.querySelector('[data-dm-upsell-price]');
    if (price) price.textContent = match ? match.priceFormatted : base.priceFormatted;

    const detail = card.querySelector('[data-dm-upsell-detail]');
    if (detail && checked) {
      const name = detail.textContent?.split(':')[0]?.trim() || '';
      detail.textContent = `${name}: ${checked.value}`;
    }

    const add = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-dm-upsell-add]'));
    const unavailable = !match || !match.available;
    if (add) {
      // A variant that went out of stock must not stay toggled on into the cart POST.
      if (unavailable && add.getAttribute('aria-pressed') === 'true') {
        add.setAttribute('aria-pressed', 'false');
        const label = add.querySelector('[data-dm-upsell-add-label]');
        const icon = add.querySelector('.dm-upsell__add-icon');
        if (label) label.textContent = 'Add';
        if (icon) icon.textContent = '+';
      }
      add.disabled = unavailable;
    }

    const error = card.querySelector('[data-dm-upsell-error]');
    if (error instanceof HTMLElement && unavailable) {
      error.textContent = 'That combination is sold out.';
      error.hidden = false;
    } else if (error instanceof HTMLElement) {
      error.hidden = true;
    }
  }

  // ------------------------------------------------------------------- cart

  async #addToCart() {
    if (!this.#canSubmit()) return;

    this.#submitting = true;
    this.#setCartError('');
    this.#renderCta();
    this.#publish();

    try {
      await this.#post();
    } catch {
      this.#setCartError(CART_ERROR_FALLBACK);
    } finally {
      this.#submitting = false;
      this.#renderCta();
      this.#publish();
    }
  }

  async #post() {
    const mainItem = {
      id: Number(this.#variantId),
      quantity: 1,
      properties: { _dm_template: 'dm-standard' },
    };

    const extraItems = [...this.#upsellItems(), ...(this.#room()?.getItems(this.dataset.productTitle) || [])];
    const items = [mainItem, ...extraItems.map(({ _title, _cardId, ...item }) => item)];

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId || '')
      .filter(Boolean);

    const payload = await this.#request(items, sections);

    if (payload.status) {
      this.dispatchEvent(new CartErrorEvent(this.id, payload.message, payload.description, payload.errors));

      // /cart/add.js is atomic: one bad line rejects the whole array. Retry item by
      // item so a single unavailable extra cannot cost the customer the main product.
      if (extraItems.length) {
        await this.#retryWithoutFailures(mainItem, extraItems, sections);
        return;
      }

      this.#setCartError(payload.description || payload.message || CART_ERROR_FALLBACK);
      return;
    }

    this.#onAdded(await this.#cartItemCount(items.length), payload.sections);
  }

  /**
   * @param {object} mainItem
   * @param {any[]} extraItems
   * @param {string[]} sections
   */
  async #retryWithoutFailures(mainItem, extraItems, sections) {
    const mainResult = await this.#request([mainItem], sections);
    if (mainResult.status) {
      this.#setCartError(
        mainResult.description || mainResult.message || CART_ERROR_FALLBACK
      );
      return;
    }

    /** @type {string[]} */
    const failed = [];

    for (const extra of extraItems) {
      const { _title, _cardId, ...item } = extra;
      const result = await this.#request([item], sections);
      if (result.status) {
        failed.push(_title);
        if (_cardId) this.#room()?.rejectCard(_cardId, 'No longer available.');
      }
    }

    if (failed.length) {
      const names = failed.join(', ');
      // The main item is already in the cart, so there is nothing here to retry.
      this.#setCartError(
        `Added, but ${names} ${failed.length === 1 ? 'is' : 'are'} no longer available and ${
          failed.length === 1 ? 'was' : 'were'
        } left out.`,
        false
      );
    }

    this.#onAdded(await this.#cartItemCount(1 + extraItems.length - failed.length));
  }

  /**
   * The header bubble treats `itemCount` from any source but the native product form as
   * the cart's TOTAL, so it must be the real cart count, not the number just added
   * (otherwise a second add resets the bubble to 1). Falls back to the added count only
   * when /cart.js cannot be read.
   * @param {number} added
   */
  async #cartItemCount(added) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return added;
      const cart = await response.json();
      return typeof cart?.item_count === 'number' ? cart.item_count : added;
    } catch {
      return added;
    }
  }

  /**
   * @param {object[]} items
   * @param {string[]} sections
   */
  async #request(items, sections) {
    /** @type {Record<string, unknown>} */
    const body = { items };
    if (sections.length) {
      body.sections = sections.join(',');
      // Render the sections in THIS page's context, as dm-cart-drawer does. Without it
      // Shopify renders them as the home page, and the header gains the index-only <h1>;
      // the next drawer morph then rebuilds the header rows around the open modal drawer.
      body.sections_url = window.location.pathname;
    }

    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const request = fetchConfig('javascript', { body: JSON.stringify(body) });

    const response = await fetch(addUrl, {
      ...request,
      headers: { ...request.headers, 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    return response.json();
  }

  /**
   * @param {number} itemCount
   * @param {unknown} [sections]
   */
  #onAdded(itemCount, sections) {
    this.dispatchEvent(
      new CartAddEvent({}, String(this.#variantId), {
        source: 'dm-standard-form',
        itemCount,
        productId: this.dataset.productId,
        sections,
      })
    );

    this.#announce('Added to cart.');
    this.dispatchEvent(new CustomEvent('dm:cart-added', { bubbles: true, composed: true }));

    this.#added = true;
    this.#renderCta();
    window.clearTimeout(this.#addedTimer);
    this.#addedTimer = window.setTimeout(() => {
      this.#added = false;
      this.#renderCta();
    }, ADDED_HOLD_MS);
  }

  /** Line items for every toggled-on upsell card. */
  #upsellItems() {
    /** @type {{ id: number, quantity: number, properties: Record<string, string>, _title: string }[]} */
    const items = [];

    for (const card of this.querySelectorAll('[data-dm-upsell]')) {
      const element = /** @type {HTMLElement} */ (card);
      const add = element.querySelector('[data-dm-upsell-add]');
      if (add?.getAttribute('aria-pressed') !== 'true') continue;

      const id = Number(element.dataset.variantId || element.dataset.defaultVariant);
      if (!id) continue;

      items.push({
        id,
        quantity: 1,
        properties: { _dm_addon_for: this.dataset.productTitle || '' },
        _title: element.dataset.productTitle || 'this item',
      });
    }

    return items;
  }

  // --------------------------------------------------------------- plumbing

  /** Main variant, every toggled upsell and every room pick, so the CTA and sticky bar agree. */
  #totalCents() {
    return (this.#variant()?.price || 0) + this.#upsellCents() + this.#roomCents;
  }

  #upsellCents() {
    let total = 0;

    for (const card of this.querySelectorAll('[data-dm-upsell]')) {
      const element = /** @type {HTMLElement} */ (card);
      const add = element.querySelector('[data-dm-upsell-add]');
      if (add?.getAttribute('aria-pressed') !== 'true') continue;

      const variants = this.#upsellVariants(element);
      const match = variants.find((variant) => String(variant.id) === element.dataset.variantId);
      total += match?.price || 0;
    }

    return total;
  }

  #formattedTotal() {
    return this.#money(this.#totalCents());
  }

  /** The room can be in the DOM before its module has upgraded it, as in dm-personalizer.js. */
  #room() {
    const element = /** @type {any} */ (this.querySelector('dm-room'));
    return typeof element?.getItems === 'function' ? element : null;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    try {
      return formatMoney(cents, format, currency);
    } catch {
      return '';
    }
  }

  /** @param {HTMLElement} card */
  #upsellVariants(card) {
    const element = card.querySelector('[data-dm-upsell-variants]');
    if (!element?.textContent) return [];
    try {
      return JSON.parse(element.textContent);
    } catch {
      return [];
    }
  }

  #readVariants() {
    const element = this.querySelector('[data-dm-std-variants]');
    if (!element?.textContent) return;
    try {
      const parsed = JSON.parse(element.textContent);
      this.#variants = Array.isArray(parsed) ? parsed : [];
    } catch {
      this.#variants = [];
    }
  }

  /**
   * The payload could not be read. Stand in the server-rendered variant for the
   * server-rendered selection (the radios Liquid checked), so that selection still
   * resolves and posts the right id. The snippet only renders this form when the product
   * is available, and selected_or_first_available_variant is then an available variant.
   */
  #useFallbackVariant() {
    const id = Number(this.dataset.fallbackVariantId);
    if (!id) return;

    const price = Number(this.dataset.fallbackPrice) || 0;
    this.#fallback = true;
    this.#variants = [
      {
        id,
        options: [...this.#selected],
        available: true,
        price,
        priceFormatted: price ? this.#money(price) : '',
        compareAtPrice: null,
        compareAtFormatted: '',
        title: '',
        featuredMedia: null,
      },
    ];
  }

  /**
   * @param {string} message
   * @param {boolean} [canRetry] false when the main item is already in the cart
   */
  #setCartError(message, canRetry = true) {
    const box = this.refs.cartError;
    const text = this.refs.cartErrorText;
    if (!(box instanceof HTMLElement)) return;

    box.hidden = !message;
    if (text instanceof HTMLElement) text.textContent = message;
    const actions = box.querySelector('.dm-alert__actions');
    if (actions instanceof HTMLElement) actions.hidden = !canRetry;
    if (message) this.#announce(message);
  }

  /** @param {string} message */
  #announce(message) {
    const announcer = this.refs.announcer;
    if (announcer instanceof HTMLElement) announcer.textContent = message;
  }

  /**
   * Tell the gallery which photo to show. A combination that does not exist still moves
   * the photo with the value just picked: the first variant carrying it stands in, so a
   * colour pick always shows that colour (logic-std-gallery-ignores-selected-variant).
   * @param {HTMLInputElement} [changed]
   */
  #emitVariantChange(changed) {
    let variantId = this.#variantId;

    if (!variantId && changed) {
      const field = changed.closest('[data-dm-option-field]');
      const index = Number(/** @type {HTMLElement | null} */ (field)?.dataset.optionIndex) || 0;
      const carrier = this.#variants.find((variant) => variant.options[index] === changed.value);
      if (carrier) variantId = carrier.id;
    }

    if (!variantId) return;
    this.dispatchEvent(
      new CustomEvent('dm:variant-change', {
        bubbles: true,
        composed: true,
        detail: { variantId },
      })
    );
  }

  /** A colorway page stops naming its color once another one is chosen (dm-pdp-hero-standard). */
  #syncColorwayName() {
    const title = document.querySelector('[data-dm-colorway-index]');
    if (!(title instanceof HTMLElement)) return;
    const index = Number(title.dataset.dmColorwayIndex);
    const color = this.#variants.find((variant) => variant.id === this.#variantId)?.options[index];
    if (!color) return;
    this.#colorwayColor ??= this.#firstColor(index);
    const own = color === this.#colorwayColor;
    title.textContent = (own ? title.dataset.dmColorwayFull : title.dataset.dmColorwayBase) || title.textContent;
    const line = document.querySelector('[data-dm-colorway-line]');
    if (line instanceof HTMLElement) line.hidden = !own;
  }

  /**
   * The page's own color: the option value whose first word is in the handle, as a room tile
   * finds it, else the variant the page opened on.
   * @param {number} index
   */
  #firstColor(index) {
    const handle = window.location.pathname.split('/').pop() || '';
    const values = [...new Set(this.#variants.map((variant) => variant.options[index]))];
    const named = values.find((value) => handle.includes(String(value).split(' ')[0].toLowerCase()));
    return named || this.#variants[0]?.options[index];
  }

  /** One event the sticky bar listens to, mirroring <dm-personalizer>'s `dm:state`. */
  #publish() {
    this.dispatchEvent(
      new CustomEvent('dm:state', { bubbles: true, composed: true, detail: this.snapshot })
    );
  }
}

if (!customElements.get('dm-standard-form')) {
  customElements.define('dm-standard-form', DmStandardForm);
}

/**
 * <dm-personalize-link> — the standard template's hand-off for a product that can only be
 * bought personalized (dm-standard-form.liquid, dm_personalize_only). No cart path: the
 * option fields only choose which variant the personalizer opens with. Once every option
 * resolves to an AVAILABLE variant the link becomes `?variant=<id>` (dm-personalizer.js
 * preselects it when no draft exists), the hero price follows it and the gallery gets the
 * same `dm:variant-change` the purchase form sends. Anything incomplete or unbuyable keeps
 * the plain product URL, so nothing is ever defaulted for the customer.
 */
class DmPersonalizeLink extends HTMLElement {
  /** @type {{ id: number, options: string[], available: boolean, priceFormatted: string, compareFormatted?: string, saveFormatted?: string }[]} */
  #variants = [];

  connectedCallback() {
    try {
      const raw = this.querySelector('[data-dm-personalize-variants]')?.textContent;
      this.#variants = raw ? JSON.parse(raw) : [];
    } catch {
      this.#variants = [];
    }
    this.addEventListener('change', this.#onChange);
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = () => {
    const variant = this.#update();
    if (variant) {
      this.dispatchEvent(
        new CustomEvent('dm:variant-change', { bubbles: true, composed: true, detail: { variantId: variant.id } })
      );
    }
  };

  /** @returns {{ id: number, options: string[], available: boolean, priceFormatted: string, compareFormatted?: string, saveFormatted?: string } | null} */
  #update() {
    const fields = [...this.querySelectorAll('[data-dm-option-field]')];
    const selected = fields.map((field) => {
      const input = field.querySelector('[data-dm-option-value]:checked');
      return input instanceof HTMLInputElement ? input.value : '';
    });
    const complete = selected.length > 0 && selected.every(Boolean);
    const variant = complete
      ? this.#variants.find(
          (v) => v.available && v.options.length === selected.length && v.options.every((o, i) => o === selected[i])
        ) || null
      : null;

    const link = this.querySelector('[data-dm-personalize-href]');
    const base = this.dataset.productUrl || '';
    if (link instanceof HTMLAnchorElement && base) {
      link.href = variant ? `${base}?variant=${encodeURIComponent(variant.id)}` : base;
    }

    // Same hooks and hide rules as DmStandardForm #renderPrice; figures come only from Liquid.
    const block = document.getElementById(`dm-hero-price-${this.dataset.sectionId || ''}`);
    if (variant && block) {
      const current = block.querySelector('[data-dm-price-current]');
      if (current instanceof HTMLElement && variant.priceFormatted) current.textContent = variant.priceFormatted;
      const compare = block.querySelector('[data-dm-price-compare]');
      if (compare instanceof HTMLElement) {
        compare.textContent = variant.compareFormatted || '';
        compare.hidden = !variant.compareFormatted;
      }
      const save = block.querySelector('[data-dm-price-save]');
      if (save instanceof HTMLElement) {
        save.textContent = variant.saveFormatted ? `Save ${variant.saveFormatted}` : '';
        save.hidden = !variant.saveFormatted;
      }
    }
    return variant;
  }
}

if (!customElements.get('dm-personalize-link')) {
  customElements.define('dm-personalize-link', DmPersonalizeLink);
}
